import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import { List, Set } from "immutable";
import React, { useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import { ClipLoader } from "react-spinners";
import { useTheme } from "styled-components";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useQuizWorkspaces } from "@domain/group/workspace/hooks";
import { ExerciseType } from "@domain/lms/course/exercise/exercise";
import { ExerciseChoiceDraft } from "@domain/lms/course/exercise/exerciseDraft";
import { useQuiz } from "@domain/lms/quiz/hooks";
import { QuizDraft } from "@domain/lms/quiz/quizDraft";
import { withId } from "@utils/domain";
import { Page } from "@ui/components/page";
import { NotFoundPage } from "@ui/pages/notFoundPage";
import { QuizEditor } from "./quizEditor";

interface QuizEditorPageProps extends React.HTMLAttributes<HTMLDivElement> {
	quizId?: string;
	workspaceId?: string;
}

export const QuizEditorPage: React.FC<QuizEditorPageProps> = ({ quizId, workspaceId, ...htmlProps }) =>
	quizId ? (
		<QuizUpdatePage quizId={quizId} {...htmlProps} />
	) : (
		<QuizCreationPage workspaceId={workspaceId} {...htmlProps} />
	);

interface QuizUpdatePageProps extends QuizEditorPageProps {
	quizId: string;
}
const QuizUpdatePage: React.FC<QuizUpdatePageProps> = ({ quizId, ...htmlProps }) => {
	const { result: initialQuiz, loading, error } = useQuiz(quizId);
	const { result: workspaces, loading: workspacesLoading } = useQuizWorkspaces(quizId);
	const [quizEditionStep, setQuizEditionStep] = useState<"MAIN" | "DETAILS">("MAIN");
	const history = useHistory();

	if (error?.code === 404) {
		return <NotFoundPage message={I18n.t("404.quiz")} />;
	}

	return (
		<Page
			{...htmlProps}
			backAction={quizEditionStep === "DETAILS" ? () => setQuizEditionStep("MAIN") : () => history.goBack()}
			backText={quizEditionStep === "DETAILS" ? I18n.t("quiz.editor.step.back") : undefined}
			header={quizEditionStep === "DETAILS" ? undefined : I18n.t("quiz.editor.header")}
		>
			{loading || workspacesLoading ? (
				<ClipLoader />
			) : (
				initialQuiz && (
					<QuizEditor
						initialQuiz={{ ...initialQuiz, workspaceIds: Set(workspaces.map(({ id }) => id)) }}
						step={quizEditionStep}
						onStepChange={setQuizEditionStep}
						quizId={quizId}
					/>
				)
			)}
		</Page>
	);
};

interface QuizCreatePageProps extends React.HTMLAttributes<HTMLDivElement> {
	workspaceId?: string;
}
const QuizCreationPage: React.FC<QuizCreatePageProps> = ({ workspaceId, ...htmlProps }) => {
	const currentProfile = useCurrentProfile();
	const [quizEditionStep, setQuizEditionStep] = useState<"MAIN" | "DETAILS">("MAIN");
	const history = useHistory();
	const theme = useTheme();

	const initialQuiz: QuizDraft = {
		title: "",
		description: ContentState.createFromText(""),
		language: currentProfile?.language ?? "ENGLISH",
		exercises: List([
			withId({
				title: "",
				type: "UNIQUE" as ExerciseType,
				choices: List([withId({ text: "", valid: false }) as ExerciseChoiceDraft]),
				comment: ContentState.createFromText(""),
				editing: true,
			}),
		]),
		status: "DRAFT",
		visibility: "PUBLIC",
		workspaceIds: Set(workspaceId ? [workspaceId] : []),
		keepOwnership: true,
		color: theme.colors.vividBlue,
	};

	return (
		<Page
			{...htmlProps}
			backAction={quizEditionStep === "DETAILS" ? () => setQuizEditionStep("MAIN") : () => history.goBack()}
			backText={quizEditionStep === "DETAILS" ? I18n.t("quiz.editor.step.back") : undefined}
			header={quizEditionStep === "DETAILS" ? undefined : I18n.t("quiz.editor.new.header")}
		>
			<QuizEditor initialQuiz={initialQuiz} step={quizEditionStep} onStepChange={setQuizEditionStep} />
		</Page>
	);
};
